import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Speedmetre from './nestedcomponent/speedmetre';
import Graph from '../Overview/graph';
import axiosInstance from '../axiosInstance';
import { FaMapMarkerAlt, FaChevronDown, FaArrowRight } from "react-icons/fa";
import GreetingDashboard from '../Dashboard/DashboardComponent/greetingDashboard';
import { getStateCode } from '../Table/utils/stateAbbreviations';

const EmptyStateCard = ({ message = "No projects available" }) => {
  return (
    <div className="flex flex-col items-center justify-center p-6 bg-gray-800/50 rounded-lg border border-dashed border-gray-600">
      <svg 
        className="w-12 h-12 text-gray-500 mb-3" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1.5} 
          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
        />
      </svg>
      <p className="text-gray-400 text-sm text-center">{message}</p>
    </div>
  );
};

const Table3 = () => {
  const [openIndex, setOpenIndex] = useState({});
  const [sections, setSections] = useState([]);
  const [isHovering, setIsHovering] = useState(null);
  const navigate = useNavigate();

  // All available sectors with their colors
  const sectorColors = {
    "Highways": "#3b82f6",       // blue
    "Commercial": "#ef4444",      // red
    "Infrastructure": "#eab308",  // yellow
    "Construction": "#ec4899",    // pink
    "Factory": "#111827",         // black
    "Residential": "#10b981",     // emerald
    "Education": "#8b5cf6",       // violet
    "Mall": "#f97316",            // orange
    "Railway": "#64748b",         // slate
    "Retail": "#84cc16",          // lime
    "Other": "#6b7280"            // gray
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axiosInstance.get('/project/allProjects', {
          headers: {
            'x-company-id': '67ce66294f28f85e4ff91e2c'
          }
        });

        const projectsData = response.data.projects;

        // Total Projects Section
        const totalProjects = {
          title: "Total Projects",
          data: [
            {
              count: `${projectsData.length}+`,
              details: projectsData.map(project => ({ ...project, project: project.project_name }))
            }
          ]
        };

        // Dynamically generate sectors based on the data
        const sectorCounts = projectsData.reduce((acc, project) => {
          acc[project.sector] = (acc[project.sector] || 0) + 1;
          return acc;
        }, {});

        // Sectors Section
        const sectors = {
          title: "SECTORS",
          data: Object.keys(sectorCounts).map(sector => ({
            name: sector,
            count: sectorCounts[sector],
            sector,
            details: projectsData
              .filter(project => project.sector === sector)
              .map(project => ({ ...project, project: project.project_name }))
          }))
        };

        // Dynamically generate States based on the data
        const stateCounts = projectsData.reduce((acc, project) => {
          acc[project.state] = (acc[project.state] || 0) + 1;
          return acc;
        }, {});

        // States Section 
        const states = {
          title: "STATES",
          data: Object.keys(stateCounts).map(state => ({
            name: state,
            count: stateCounts[state],
            state,
            details: projectsData
              .filter(project => project.state === state)
              .map(project => ({ ...project, project: project.project_name }))
          }))
        };

        // Sort projects by cost to get the biggest projects
        const sortedByCost = projectsData.sort((a, b) => b.cost - a.cost);
        
        const biggestProjects = {
          title: "BIGGEST PROJECTS",
          data: sortedByCost.slice(0, 5).map(project => ({
            name: project.project_name.replace(/^-/, ''),
            sector: project.sector,
            state: getStateCode(project.state),
            cost: `₹${(project.cost * 75).toLocaleString()}`,
            details: [project]
          }))
        };

        setSections([totalProjects, sectors, states, biggestProjects]);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    fetchProjects();
  }, []);

  const toggleSection = (category, index) => {
    setOpenIndex((prev) => ({
      ...prev,
      [category]: prev[category] === index ? null : index,
    }));
  };

  const handleProjectClick = (project) => {
    navigate('/detail', { state: { project } });
  };

  const getSectorColor = (sectorName) => {
    return sectorColors[sectorName] || "#10b981"; // default to emerald if not found
  };

  const truncateText = (text, maxLength = 20) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const formatBiggestProject = (project) => {
    return (
      <div className="flex flex-col w-full">
        <div className="flex justify-between w-full items-center">
          <span className="text-sm font-medium truncate max-w-[150px]">
            {truncateText(project.name)}
          </span>
          <span className="text-xs bg-gray-800/50 px-2 py-1 rounded-full">
            {project.sector}-{project.state}
          </span>
        </div>
        <span className="text-sm font-bold text-blue-300 mt-1">
          {project.cost}
        </span>
      </div>
    );
  };

  return (
    <div className="container-fluid p-3 flex-col lg:flex-row gap-5 h-full w-full overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
      <div className="w-full h-auto">
        <div className="w-full flex flex-col gap-4">
          <GreetingDashboard />
          
          {/* Total Projects Section */}
          {sections.some(section => section.title === "Total Projects") && (
            <div className="bg-gray-900 rounded-xl shadow-lg overflow-hidden border border-gray-700">
              {sections
                .find(section => section.title === "Total Projects")
                ?.data.map((item, index) => (
                  <div key={index} className="group">
                    <button
                      className={`w-full text-left p-4 flex justify-between items-center transition-all duration-300 ${
                        openIndex["Total Projects"] === index 
                          ? "bg-gray-700 text-white"
                          : "bg-gray-800 hover:bg-gray-700 text-gray-100"
                      }`}
                      onClick={() => toggleSection("Total Projects", index)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
                          openIndex["Total Projects"] === index ? "bg-white" : "bg-blue-400"
                        }`}></div>
                        <span className="text-lg font-medium">Total Projects</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`text-2xl font-bold bg-gradient-to-r from-blue-300 to-blue-100 bg-clip-text text-transparent`}>
                          {item.count}
                        </span>
                        <div className={`transition-transform duration-300 ${
                          openIndex["Total Projects"] === index ? "rotate-180" : ""
                        }`}>
                          <FaChevronDown className={`text-lg ${
                            openIndex["Total Projects"] === index ? "text-blue-200" : "text-blue-400"
                          }`} />
                        </div>
                      </div>
                    </button>

                    <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      openIndex["Total Projects"] === index
                        ? "max-h-[500px] opacity-100"
                        : "max-h-0 opacity-0"
                    }`}>
                      <div className="p-3 bg-gray-800/50">
                        {item.details.length > 0 ? (
                          <div className="relative overflow-x-auto rounded-lg border border-gray-700" style={{ maxHeight: '300px', overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            <table className="w-full text-sm text-left text-gray-300">
                              <thead className="text-xs bg-gray-700 text-blue-300 sticky top-0">
                                <tr>
                                  <th scope="col" className="px-4 py-2">
                                    Project Name
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-700">
                                {item.details.map((detail, idx) => (
                                  <tr
                                    key={idx}
                                    className="bg-gray-800/50 hover:bg-gray-700/70 transition-colors duration-150 cursor-pointer"
                                    onClick={() => handleProjectClick(detail)}
                                    onMouseEnter={() => setIsHovering(`total-${idx}`)}
                                    onMouseLeave={() => setIsHovering(null)}
                                  >
                                    <td className="px-4 py-3 font-medium">
                                      <div className="flex items-center justify-between">
                                        <span className="truncate max-w-[180px]">{detail.project}</span>
                                        <span className={`transition-all duration-300 ${isHovering === `total-${idx}` ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0"}`}>
                                          <FaArrowRight className="text-blue-400" />
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <EmptyStateCard message="No projects found" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
 
          {/* Speedometer Section */}
          <Speedmetre />

          {/* Graph Component */}
          <Graph />

          {/* Other Sections (excluding Total Projects) */}
          {sections
            .filter(section => section.title !== "Total Projects")
            .map((section, sectionIdx) => (
              <div 
                key={sectionIdx} 
                className="bg-gray-900 rounded-xl shadow-lg overflow-hidden border border-gray-700"
              >
                <h2 className="text-lg font-semibold p-4 text-white bg-gray-800 border-b border-gray-700">
                  <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                    {section.title}
                  </span>
                </h2>
                <div className="p-3 space-y-2">
                  {section.data.map((item, index) => (
                    <div key={index} className="group">
                      <button
                        className={`w-full text-left p-4 rounded-lg font-medium flex items-center justify-between transition-all duration-300 ${
                          openIndex[section.title] === index 
                            ? "bg-gray-700 text-white"
                            : "bg-gray-800 hover:bg-gray-700 text-gray-100"
                        }`}
                        onClick={() => toggleSection(section.title, index)}
                      >
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          {section.title === "BIGGEST PROJECTS" ? (
                            formatBiggestProject(item)
                          ) : (
                            <>
                              <span className="text-sm font-medium truncate">
                                {item.name || item.count}
                              </span>
                              {item.name && (
                                <span className="text-xs bg-gray-700/50 px-2 py-1 rounded-full whitespace-nowrap ml-2">
                                  {item.count}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 ml-2">
                          {item.name && Object.keys(sectorColors).includes(item.name) && (
                            <FaMapMarkerAlt
                              size={16}
                              color={getSectorColor(item.name)}
                              className="transition-all duration-300"
                            />
                          )}
                          <div className={`transition-transform duration-300 ${
                            openIndex[section.title] === index ? "rotate-180" : ""
                          }`}>
                            <FaChevronDown className={`text-lg ${
                              openIndex[section.title] === index ? "text-white" : "text-blue-400"
                            }`} />
                          </div>
                        </div>
                      </button>

                      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                        openIndex[section.title] === index
                          ? "max-h-[500px] opacity-100 mt-1"
                          : "max-h-0 opacity-0"
                      }`}>
                        <div className="p-2 bg-gray-800/50">
                          {item.details.length > 0 ? (
                            <div className="relative overflow-x-auto rounded-lg border border-gray-700">
                              <table className="w-full text-sm text-left text-gray-300">
                                <thead className="text-xs bg-gray-700 text-blue-300">
                                  <tr>
                                    <th scope="col" className="px-4 py-2">
                                      Project Name
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-700">
                                  {item.details.map((detail, idx) => (
                                    <tr
                                      key={idx}
                                      className="bg-gray-800/50 hover:bg-gray-700/70 transition-colors duration-150 cursor-pointer"
                                      onClick={() => handleProjectClick(detail)}
                                      onMouseEnter={() => setIsHovering(`${sectionIdx}-${index}-${idx}`)}
                                      onMouseLeave={() => setIsHovering(null)}
                                    >
                                      <td className="px-4 py-3 font-medium">
                                        <div className="flex items-center justify-between">
                                          <span className="truncate max-w-[180px]">{detail.project_name || detail.project}</span>
                                          <span className={`transition-all duration-300 ${isHovering === `${sectionIdx}-${index}-${idx}` ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0"}`}>
                                            <FaArrowRight className="text-blue-400" />
                                          </span>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <EmptyStateCard message={`No projects in this ${section.title === "SECTORS" ? "sector" : section.title === "STATES" ? "state" : "category"}`} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Table3;

// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import Speedmetre from './nestedcomponent/speedmetre';
// import Graph from '../Overview/graph';
// import axiosInstance from '../axiosInstance';
// import { FaMapMarkerAlt, FaChevronDown, FaChevronUp, FaArrowRight } from "react-icons/fa";
// import GreetingDashboard from '../Dashboard/DashboardComponent/greetingDashboard';
// import { getStateCode } from '../Table/utils/stateAbbreviations';
// const Table3 = () => {
//   const [openIndex, setOpenIndex] = useState({});
//   const [projects, setProjects] = useState([]);
//   const [selectedCity, setSelectedCity] = useState('');
//   const [sections, setSections] = useState([]);
//   const [isHovering, setIsHovering] = useState(null);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const response = await axiosInstance.get('/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
//           }
//         });

//         const projectsData = response.data.projects;

//         // Total Projects Section
//         const totalProjects = {
//           title: "Total Projects",
//           data: [
//             {
//               count: `${projectsData.length}+`,
//               details: projectsData.map(project => ({ ...project, project: project.project_name }))
//             }
//           ]
//         };

       

//         // Dynamically generate sectors based on the data
//         const sectorCounts = projectsData.reduce((acc, project) => {
//           acc[project.sector] = (acc[project.sector] || 0) + 1;
//           return acc;
//         }, {});

//         // Sectors Section
//         const sectors = {
//           title: "SECTORS",
//           data: Object.keys(sectorCounts).map(sector => ({
//             name: sector,
//             count: sectorCounts[sector],
//             sector,
//             details: projectsData
//               .filter(project => project.sector === sector)
//               .map(project => ({ ...project, project: project.project_name }))
//           }))
//         };

//         // Dynamically generate States based on the data
//         const stateCounts = projectsData.reduce((acc, project) => {
//           acc[project.state] = (acc[project.state] || 0) + 1;
//           return acc;
//         }, {});

//         // States Section 
//         const states = {
//           title: "STATES",
//           data: Object.keys(stateCounts).map(state => ({
//             name: state,
//             count: stateCounts[state],
//             state,
//             details: projectsData
//               .filter(project => project.state === state)
//               .map(project => ({ ...project, project: project.project_name }))
//           }))
//         };

//         // Sort projects by cost to get the biggest projects
//         const sortedByCost = projectsData.sort((a, b) => b.cost - a.cost);
//         // Biggest Projects Section
//         // const biggestProjects = {
//         //   title: "BIGGEST PROJECTS",
          
//         //   data: sortedByCost.slice(0, 5).map(project => ({
//         //     name: project.project_name.replace(/^-/, ''),
//         //     sector: project.sector,
//         //     state: project.state,
//         //     cost: `₹${(project.cost * 75).toLocaleString()}`,
//         //     details: [project]
//         //   }))
//         // };


//         // const biggestProjects = {
//         //   title: "BIGGEST PROJECTS",
//         //   data: sortedByCost.slice(0, 5).map(project => {
//         //     const shortState = stateAbbreviations[project.state] || project.state; // fallback if not in map
//         //     return {
//         //       name: project.project_name.replace(/^-/, ''),
//         //       sector: project.sector,
//         //       state: shortState,
//         //       cost: `₹${(project.cost * 75).toLocaleString()}`,
//         //       details: [project]
//         //     };
//         //   })
//         // };
        

//         const biggestProjects = {
//           title: "BIGGEST PROJECTS",
//           data: sortedByCost.slice(0, 5).map(project => ({
//             name: project.project_name.replace(/^-/, ''),
//             sector: project.sector,
//             state: getStateCode(project.state), // ✅ Abbreviated state
//             cost: `₹${(project.cost * 75).toLocaleString()}`,
//             details: [project]
//           }))
//         };

//         setSections([totalProjects, sectors, states, biggestProjects]);
//       } catch (error) {
//         console.error('Error fetching projects:', error);
//       }
//     };

//     fetchProjects();
//   }, []);

//   const toggleSection = (category, index) => {
//     setOpenIndex((prev) => ({
//       ...prev,
//       [category]: prev[category] === index ? null : index,
//     }));
//   };

//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } });
//   };

//   const getSectorColor = (sectorName) => {
//     switch (sectorName) {
//       case "Highways": return "blue";
//       case "Commercial": return "red";
//       case "Infrastructure": return "yellow";
//       case "Construction": return "pink";
//       case "Factory": return "black";
//       default: return "green";
//     }
//   };

//   const truncateText = (text, maxLength = 20) => {
//     return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
//   };

//   const formatBiggestProject = (project) => {
//     return (
//       <div className="flex flex-col w-full">
//         <div className="flex justify-between w-full items-center">
//           <span className="text-sm font-medium truncate max-w-[150px]">
//             {truncateText(project.name)}
//           </span>
//           <span className="text-xs bg-gray-800/50 px-2 py-1 rounded-full">
//             {project.sector}-{project.state}
//           </span>
//         </div>
//         <span className="text-sm font-bold text-blue-300 mt-1">
//           {project.cost}
//         </span>
//       </div>
//     );
//   };

//   return (
//     <div className="container-fluid p-3 flex-col lg:flex-row gap-5 h-full w-full overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//       <div className="w-full h-auto">
//         <div className="w-full flex flex-col gap-4">
//           <GreetingDashboard />
          
//           {/* Total Projects Section */}
//           {sections.some(section => section.title === "Total Projects") && (
//             <div className="bg-gray-900 rounded-xl shadow-lg overflow-hidden border border-gray-700">
//               {sections
//                 .find(section => section.title === "Total Projects")
//                 ?.data.map((item, index) => (
//                   <div key={index} className="group">
//                     <button
//                       className={`w-full text-left p-4 flex justify-between items-center transition-all duration-300 ${
//                         openIndex["Total Projects"] === index 
//                           ? "bg-gray-700 text-white"
//                           : "bg-gray-800 hover:bg-gray-700 text-gray-100"
//                       }`}
//                       onClick={() => toggleSection("Total Projects", index)}
//                     >
//                       <div className="flex items-center space-x-3">
//                         <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
//                           openIndex["Total Projects"] === index ? "bg-white" : "bg-blue-400"
//                         }`}></div>
//                         <span className="text-lg font-medium">Total Projects</span>
//                       </div>
//                       <div className="flex items-center space-x-3">
//                         <span className={`text-2xl font-bold bg-gradient-to-r from-blue-300 to-blue-100 bg-clip-text text-transparent`}>
//                           {item.count}
//                         </span>
//                         <div className={`transition-transform duration-300 ${
//                           openIndex["Total Projects"] === index ? "rotate-180" : ""
//                         }`}>
//                           <FaChevronDown className={`text-lg ${
//                             openIndex["Total Projects"] === index ? "text-blue-200" : "text-blue-400"
//                           }`} />
//                         </div>
//                       </div>
//                     </button>

//                     <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
//                       openIndex["Total Projects"] === index
//                         ? "max-h-[500px] opacity-100"
//                         : "max-h-0 opacity-0"
//                     }`}>
//                       <div className="p-3 bg-gray-800/50">
//                         <div className="relative overflow-x-auto rounded-lg border border-gray-700" style={{ maxHeight: '300px', overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//                           <table className="w-full text-sm text-left text-gray-300">
//                             <thead className="text-xs bg-gray-700 text-blue-300 sticky top-0">
//                               <tr>
//                                 <th scope="col" className="px-4 py-2">
//                                   Project Name
//                                 </th>
//                               </tr>
//                             </thead>
//                             <tbody className="divide-y divide-gray-700">
//                               {item.details.map((detail, idx) => (
//                                 <tr
//                                   key={idx}
//                                   className="bg-gray-800/50 hover:bg-gray-700/70 transition-colors duration-150 cursor-pointer"
//                                   onClick={() => handleProjectClick(detail)}
//                                   onMouseEnter={() => setIsHovering(`total-${idx}`)}
//                                   onMouseLeave={() => setIsHovering(null)}
//                                 >
//                                   <td className="px-4 py-3 font-medium">
//                                     <div className="flex items-center justify-between">
//                                       <span className="truncate max-w-[180px]">{detail.project}</span>
//                                       <span className={`transition-all duration-300 ${isHovering === `total-${idx}` ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0"}`}>
//                                         <FaArrowRight className="text-blue-400" />
//                                       </span>
//                                     </div>
//                                   </td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </table>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//             </div>
//           )}
 
//           {/* Speedometer Section */}
//           <Speedmetre />

//           {/* Graph Component */}
//           <Graph />

//           {/* Other Sections (excluding Total Projects) */}
//           {sections
//             .filter(section => section.title !== "Total Projects")
//             .map((section, sectionIdx) => (
//               <div 
//                 key={sectionIdx} 
//                 className="bg-gray-900 rounded-xl shadow-lg overflow-hidden border border-gray-700"
//               >
//                 <h2 className="text-lg font-semibold p-4 text-white bg-gray-800 border-b border-gray-700">
//                   <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
//                     {section.title}
//                   </span>
//                 </h2>
//                 <div className="p-3 space-y-2">
//                   {section.data.map((item, index) => (
//                     <div key={index} className="group">
//                       <button
//                         className={`w-full text-left p-4 rounded-lg font-medium flex items-center justify-between transition-all duration-300 ${
//                           openIndex[section.title] === index 
//                             ? "bg-gray-700 text-white"
//                             : "bg-gray-800 hover:bg-gray-700 text-gray-100"
//                         }`}
//                         onClick={() => toggleSection(section.title, index)}
//                       >
//                         <div className="flex items-center space-x-3 min-w-0 flex-1">
//                           {section.title === "BIGGEST PROJECTS" ? (
//                             formatBiggestProject(item)
//                           ) : (
//                             <>
//                               <span className="text-sm font-medium truncate">
//                                 {item.name || item.count}
//                               </span>
//                               {item.name && (
//                                 <span className="text-xs bg-gray-700/50 px-2 py-1 rounded-full whitespace-nowrap ml-2">
//                                   {item.count}
//                                 </span>
//                               )}
//                             </>
//                           )}
//                         </div>
//                         <div className="flex items-center space-x-2 ml-2">
//                           {item.name && ["Highways", "Infrastructure", "Commercial", "Construction", "Factory"].includes(item.name) && (
//                             <FaMapMarkerAlt
//                               size={16}
//                               color={item.name === "Highways" ? "#3b82f6" : 
//                                     item.name === "Commercial" ? "#ef4444" : 
//                                     item.name === "Infrastructure" ? "#eab308" : 
//                                     item.name === "Construction" ? "#ec4899" : 
//                                     item.name === "Factory" ? "#111827" : "#10b981"}
//                               className="transition-all duration-300"
//                             />
//                           )}
//                           <div className={`transition-transform duration-300 ${
//                             openIndex[section.title] === index ? "rotate-180" : ""
//                           }`}>
//                             <FaChevronDown className={`text-lg ${
//                               openIndex[section.title] === index ? "text-white" : "text-blue-400"
//                             }`} />
//                           </div>
//                         </div>
//                       </button>

//                       <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
//                         openIndex[section.title] === index
//                           ? "max-h-[500px] opacity-100 mt-1"
//                           : "max-h-0 opacity-0"
//                       }`}>
//                         <div className="p-2 bg-gray-800/50">
//                           <div className="relative overflow-x-auto rounded-lg border border-gray-700">
//                             <table className="w-full text-sm text-left text-gray-300">
//                               <thead className="text-xs bg-gray-700 text-blue-300">
//                                 <tr>
//                                   <th scope="col" className="px-4 py-2">
//                                     Project Name
//                                   </th>
//                                 </tr>
//                               </thead>
//                               <tbody className="divide-y divide-gray-700">
//                                 {item.details.map((detail, idx) => (
//                                   <tr
//                                     key={idx}
//                                     className="bg-gray-800/50 hover:bg-gray-700/70 transition-colors duration-150 cursor-pointer"
//                                     onClick={() => handleProjectClick(detail)}
//                                     onMouseEnter={() => setIsHovering(`${sectionIdx}-${index}-${idx}`)}
//                                     onMouseLeave={() => setIsHovering(null)}
//                                   >
//                                     <td className="px-4 py-3 font-medium">
//                                       <div className="flex items-center justify-between">
//                                         <span className="truncate max-w-[180px]">{detail.project_name || detail.project}</span>
//                                         <span className={`transition-all duration-300 ${isHovering === `${sectionIdx}-${index}-${idx}` ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0"}`}>
//                                           <FaArrowRight className="text-blue-400" />
//                                         </span>
//                                       </div>
//                                     </td>
//                                   </tr>
//                                 ))}
//                               </tbody>
//                             </table>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             ))}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Table3;

// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import Speedmetre from './nestedcomponent/speedmetre';
// import Graph from '../Overview/graph';
// import axiosInstance from '../axiosInstance';
// import { FaMapMarkerAlt, FaChevronDown, FaChevronUp, FaArrowRight } from "react-icons/fa";
// import GreetingDashboard from '../Dashboard/DashboardComponent/greetingDashboard';

// const Table3 = () => {
//   const [openIndex, setOpenIndex] = useState({});
//   const [projects, setProjects] = useState([]);
//   const [selectedCity, setSelectedCity] = useState('');
//   const [sections, setSections] = useState([]);
//   const [isHovering, setIsHovering] = useState(null);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const response = await axiosInstance.get('/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
//           }
//         });

//         const projectsData = response.data.projects;

//         // Total Projects Section
//         const totalProjects = {
//           title: "Total Projects",
//           data: [
//             {
//               count: `${projectsData.length}+`,
//               details: projectsData.map(project => ({ ...project, project: project.project_name }))
//             }
//           ]
//         };

//         // Dynamically generate sectors based on the data
//         const sectorCounts = projectsData.reduce((acc, project) => {
//           acc[project.sector] = (acc[project.sector] || 0) + 1;
//           return acc;
//         }, {});

//         // Sectors Section
//         const sectors = {
//           title: "SECTORS",
//           data: Object.keys(sectorCounts).map(sector => ({
//             name: sector,
//             count: sectorCounts[sector],
//             sector,
//             details: projectsData
//               .filter(project => project.sector === sector)
//               .map(project => ({ ...project, project: project.project_name }))
//           }))
//         };

//         // Dynamically generate States based on the data
//         const stateCounts = projectsData.reduce((acc, project) => {
//           acc[project.state] = (acc[project.state] || 0) + 1;
//           return acc;
//         }, {});

//         // States Section 
//         const states = {
//           title: "STATES",
//           data: Object.keys(stateCounts).map(state => ({
//             name: state,
//             count: stateCounts[state],
//             state,
//             details: projectsData
//               .filter(project => project.state === state)
//               .map(project => ({ ...project, project: project.project_name }))
//           }))
//         };

//         // Sort projects by cost to get the biggest projects
//         const sortedByCost = projectsData.sort((a, b) => b.cost - a.cost);

//         // Biggest Projects Section
//         const biggestProjects = {
//           title: "BIGGEST PROJECTS",
//           data: sortedByCost.slice(0, 5).map(project => ({
//             name: project.project_name.replace(/^-/, ''),
//             sector: project.sector,
//             state: project.state,
//             cost: `₹${(project.cost * 75).toLocaleString()}`,
//             details: [project]
//           }))
//         };

//         setSections([totalProjects, sectors, states, biggestProjects]);
//       } catch (error) {
//         console.error('Error fetching projects:', error);
//       }
//     };

//     fetchProjects();
//   }, []);

//   const toggleSection = (category, index) => {
//     setOpenIndex((prev) => ({
//       ...prev,
//       [category]: prev[category] === index ? null : index,
//     }));
//   };

//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } });
//   };

//   const getSectorColor = (sectorName) => {
//     switch (sectorName) {
//       case "Highways": return "blue";
//       case "Commercial": return "red";
//       case "Infrastructure": return "yellow";
//       case "Construction": return "pink";
//       case "Factory": return "black";
//       default: return "green";
//     }
//   };

//   const truncateText = (text, maxLength = 20) => {
//     return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
//   };

//   const formatBiggestProject = (project) => {
//     return (
//       <div className="flex flex-col w-full">
//         <div className="flex justify-between w-full items-baseline">
//           <span className="text-sm md:text-base font-medium truncate max-w-[120px] sm:max-w-[180px]">
//             {truncateText(project.name)}
//           </span>
//           <span className="text-xs text-gray-300 ml-2 whitespace-nowrap">
//             ({project.sector}-{project.state})
//           </span>
//         </div>
//         <span className="text-sm md:text-base font-bold text-blue-300 mt-1">
//           {project.cost}
//         </span>
//       </div>
//     );
//   };

//   return (
//     <div className="container-fluid p-3 flex-col lg:flex-row gap-5 h-full w-full overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//       <div className="w-full h-auto">
//         <div className="w-full flex flex-col gap-6">
//           <GreetingDashboard />
          
//           {/* Total Projects Section */}
//           {sections.some(section => section.title === "Total Projects") && (
//             <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-700 transition-all duration-500">
//               {sections
//                 .find(section => section.title === "Total Projects")
//                 ?.data.map((item, index) => (
//                   <div key={index} className="group">
//                     <button
//                       className={`w-full text-left p-4 sm:p-6 rounded-t-xl font-medium flex justify-between items-center transition-all duration-300 ${
//                         openIndex["Total Projects"] === index 
//                           ? "bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-inner"
//                           : "bg-gray-800 hover:bg-gray-700 text-gray-100"
//                       }`}
//                       onClick={() => toggleSection("Total Projects", index)}
//                     >
//                       <div className="flex items-center space-x-3 sm:space-x-4">
//                         <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
//                           openIndex["Total Projects"] === index ? "bg-white scale-125" : "bg-blue-400"
//                         }`}></div>
//                         <span className="text-lg sm:text-xl font-semibold">Total Projects</span>
//                       </div>
//                       <div className="flex items-center space-x-3 sm:space-x-4">
//                         <span className={`text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-300 to-blue-100 bg-clip-text text-transparent transition-all duration-300 ${
//                           openIndex["Total Projects"] === index ? "scale-110" : ""
//                         }`}>
//                           {item.count}
//                         </span>
//                         <div className={`transition-transform duration-300 ${
//                           openIndex["Total Projects"] === index ? "-rotate-180" : "rotate-180"
//                         }`}>  
//                           {openIndex["Total Projects"] === index ? (
//                             <FaChevronDown className="text-blue-200 text-base sm:text-lg " />
//                           ) : (
//                             <FaChevronUp className="text-blue-400 text-base sm:text-lg" />
//                           )}
//                         </div>
//                       </div>
//                     </button>

//                     <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
//                       openIndex["Total Projects"] === index
//                         ? "max-h-[500px] opacity-100"
//                         : "max-h-0 opacity-0"
//                     }`}>
//                       <div className="p-3 sm:p-4 bg-gray-800/50 backdrop-blur-sm">
//                         <div className="relative overflow-x-auto rounded-lg border border-gray-700" style={{ maxHeight: '300px', overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//                           <table className="w-full text-xs sm:text-sm text-left text-gray-300">
//                             <thead className="uppercase bg-gray-700 text-blue-300 sticky top-0">
//                               <tr>
//                                 <th scope="col" className="px-4 py-2 sm:px-6 sm:py-3">
//                                   Project Name
//                                 </th>
//                               </tr>
//                             </thead>
//                             <tbody className="divide-y divide-gray-700">
//                               {item.details.map((detail, idx) => (
//                                 <tr
//                                   key={idx}
//                                   className="bg-gray-800/50 hover:bg-gray-700/70 transition-colors duration-150 cursor-pointer"
//                                   onClick={() => handleProjectClick(detail)}
//                                   onMouseEnter={() => setIsHovering(`total-${idx}`)}
//                                   onMouseLeave={() => setIsHovering(null)}
//                                 >
//                                   <td className="px-4 py-3 sm:px-6 sm:py-4 font-medium">
//                                     <div className="flex items-center justify-between">
//                                       <span className="truncate max-w-[120px] sm:max-w-xs">{detail.project}</span>
//                                       <span className={`transition-all duration-300 ${isHovering === `total-${idx}` ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0"}`}>
//                                         <FaArrowRight className="text-blue-400" />
//                                       </span>
//                                     </div>
//                                   </td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </table>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//             </div>
//           )}

//           {/* Speedometer Section */}
//           <Speedmetre />

//           {/* Graph Component */}
//           <Graph />

//           {/* Other Sections (excluding Total Projects) */}
//           {sections
//             .filter(section => section.title !== "Total Projects")
//             .map((section, sectionIdx) => (
//               <div 
//                 key={sectionIdx} 
//                 className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-700 transition-all duration-500"
//               >
//                 <h2 className="text-lg sm:text-xl font-semibold p-4 sm:p-6 text-white bg-gray-800 border-b border-gray-700 flex items-center">
//                   <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
//                     {section.title}
//                   </span>
//                 </h2>
//                 <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
//                   {section.data.map((item, index) => (
//                     <div key={index} className="group">
//                       <button
//                         className={`w-full text-left p-3 sm:p-5 rounded-xl font-medium flex items-center justify-between transition-all duration-300 ${
//                           openIndex[section.title] === index 
//                             ? "bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-inner"
//                             : "bg-gray-800 hover:bg-gray-700 text-gray-100"
//                         }`}
//                         onClick={() => toggleSection(section.title, index)}
//                       >
//                         <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
//                           {item.name && (
//                             <div 
//                               className={`w-2 h-2 rounded-full bg-${getSectorColor(item.name || item.sector)}-500 transition-all duration-300 ${
//                                 openIndex[section.title] === index ? "scale-125" : ""
//                               }`}
//                             ></div>
//                           )}
//                           {section.title === "BIGGEST PROJECTS" ? (
//                             formatBiggestProject(item)
//                           ) : (
//                             <>
//                               <span className="text-sm sm:text-base font-medium truncate">
//                                 {item.name || item.count}
//                               </span>
//                               {item.name && (
//                                 <span className="text-xs sm:text-sm bg-gray-700/50 px-2 py-1 rounded-full transition-all duration-300 group-hover:bg-gray-600/50 whitespace-nowrap ml-2">
//                                   {item.count}
//                                 </span>
//                               )}
//                             </>
//                           )}
//                         </div>
//                         <div className="flex items-center space-x-2 ml-2">
//                           {item.name && ["Highways", "Infrastructure", "Commercial", "Construction", "Factory"].includes(item.name) && (
//                             <FaMapMarkerAlt
//                               size={16}
//                               color={getSectorColor(item.name)}
//                               className="transition-all duration-300"
//                             />
//                           )}
//                           <div className={`transition-transform duration-300 ${
//                             openIndex[section.title] === index ? "-rotate-180" : "rotate-180"
//                           }`}>
//                             {openIndex[section.title] === index ? (
//                               <FaChevronDown className="text-blue-200" />
//                             ) : (
//                               <FaChevronUp className="text-blue-400" />
//                             )}
//                           </div>
//                         </div>
//                       </button>

//                       <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
//                         openIndex[section.title] === index
//                           ? "max-h-[500px] opacity-100 mt-1 sm:mt-2"
//                           : "max-h-0 opacity-0"
//                       }`}>
//                         <div className="p-2 sm:p-3 bg-gray-800/50 backdrop-blur-sm">
//                           <div className="relative overflow-x-auto rounded-lg border border-gray-700">
//                             <table className="w-full text-xs sm:text-sm text-left text-gray-300">
//                               <thead className="uppercase bg-gray-700/80 text-blue-300">
//                                 <tr>
//                                   <th scope="col" className="px-4 py-2 sm:px-6 sm:py-3">
//                                     Project Name
//                                   </th>
//                                 </tr>
//                               </thead>
//                               <tbody className="divide-y divide-gray-700">
//                                 {item.details.map((detail, idx) => (
//                                   <tr
//                                     key={idx}
//                                     className="bg-gray-800/50 hover:bg-gray-700/70 transition-colors duration-150 cursor-pointer"
//                                     onClick={() => handleProjectClick(detail)}
//                                     onMouseEnter={() => setIsHovering(`${sectionIdx}-${index}-${idx}`)}
//                                     onMouseLeave={() => setIsHovering(null)}
//                                   >
//                                     <td className="px-4 py-3 sm:px-6 sm:py-4 font-medium">
//                                       <div className="flex items-center justify-between">
//                                         <span className="truncate max-w-[120px] sm:max-w-none">{detail.project_name || detail.project}</span>
//                                         <span className={`transition-all duration-300 ${isHovering === `${sectionIdx}-${index}-${idx}` ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0"}`}>
//                                           <FaArrowRight className="text-blue-400" />
//                                         </span>
//                                       </div>
//                                     </td>
//                                   </tr>
//                                 ))}
//                               </tbody>
//                             </table>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             ))}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Table3;

// add new feature
// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import axios from 'axios'; // Import axios for API calls
// import Speedmetre from './nestedcomponent/speedmetre';
// import Worldmapcard from './worldmapcard';
// import Locationselect from './locationselect';
// import Graph from '../Overview/graph';

// const Table3 = () => {
//   const [openIndex, setOpenIndex] = useState({});
//   const [projects, setProjects] = useState([]);
//   const [selectedCity, setSelectedCity] = useState('');
//   const [sections, setSections] = useState([]); // State to store dynamic sections
//   const navigate = useNavigate();

//   // Fetch projects from the API
//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const response = await axios.get('http://localhost:3002/api/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
//           }
//         });

//         // Log the API response to debug
//         console.log('API Response:', response.data);

//         // Access the `projects` array from the response
//         const projectsData = response.data.projects;

//         // Transform the API data into the required format for sections
//         const totalProjects = {
//           title: "Total Projects",
//           data: [
//             {
//               count: `${projectsData.length}+`,
//               details: projectsData.map(project => ({ ...project, project: project.project_name })) // Pass the entire project object
//             }
//           ]
//         };

//         // Dynamically generate sectors based on the data
//         const sectorCounts = projectsData.reduce((acc, project) => {
//           acc[project.sector] = (acc[project.sector] || 0) + 1;
//           return acc;
//         }, {});

//         const sectors = {
//           title: "SECTORS",
//           data: Object.keys(sectorCounts).map(sector => ({
//             name: sector,
//             count: sectorCounts[sector],
//             details: projectsData
//               .filter(project => project.sector === sector)
//               .map(project => ({ ...project, project: project.project_name })) // Pass the entire project object
//           }))
//         };

//         // Sort projects by cost to get the biggest projects
//         const sortedByCost = projectsData.sort((a, b) => b.cost - a.cost);
//         const biggestProjects = {
//           title: "BIGGEST PROJECTS",
//           data: sortedByCost.slice(0, 5).map(project => ({
//             // name: project.project_name,
//             count: `${project.project_name} (${project.sector}-${project.state}) - $${project.cost}`, // Display project name, sector, state, and cost

//             details: [project] // Pass the entire project object
//           }))
//         };

//         // Set the sections with dynamic data
//         setSections([totalProjects, sectors, biggestProjects]);
//       } catch (error) {
//         console.error('Error fetching projects:', error);
//       }
//     };

//     fetchProjects();
//   }, []);

//   const toggleSection = (category, index) => {
//     setOpenIndex((prev) => ({
//       ...prev,
//       [category]: prev[category] === index ? null : index,
//     }));
//   };

//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } }); // Pass the entire project object
//   };

//   return (
//     <div
//       className="container-fluid p-3 flex-col lg:flex-row gap-5"
//       style={{
//         overflowY: 'auto',
//         height: '100%',
//         width: '100%',
//         scrollbarWidth: 'none',
//         msOverflowStyle: 'none'
//       }}
//     >
//       <div style={{ width: '100%', height: 'auto' }}>
//         <div className="w-full flex flex-col gap-3">

//           {/* Total Projects Section */}
//           {sections.some(section => section.title === "Total Projects") && (
//             <div>
//               <h2 className="text-xl font-semibold mb-4 text-white uppercase">Total Projects</h2>
//               <div className="bg-gray-800 p-6 rounded-2xl shadow-lg w-full">
//                 {sections
//                   .find(section => section.title === "Total Projects")
//                   ?.data.map((item, index) => (
//                     <div key={index} className="mb-1">
//                       <button
//                         className="w-full text-left p-3 bg-gray-200 rounded-lg font-medium"
//                         onClick={() => toggleSection("Total Projects", index)}
//                       >
//                         Total Projects - <span className="text-2xl font-bold">{item.count}</span>
//                       </button>
//                       <div
//                         style={{
//                           maxHeight: openIndex["Total Projects"] === index ? "500px" : "0px",
//                           overflow: 'hidden',
//                           transition: "max-height 0.5s ease-in-out, padding 0.3s ease-in-out",
//                           background: "#f3f4f6",
//                           padding: openIndex["Total Projects"] === index ? "12px" : "0px",
//                           borderRadius: "8px",
//                           marginTop: "8px",
//                         }}
//                       >
//                         <table className="w-full text-sm">
//                           <thead>
//                             <tr className="border-b">
//                               <th className="text-left p-2">Project Name</th>
//                             </tr>
//                           </thead>
//                           <div className="max-h-96 overflow-y-auto">
//                             <tbody>
//                               {item.details.map((detail, idx) => (
//                                 <tr
//                                   key={idx}
//                                   className="border-b hover:bg-gray-100 cursor-pointer"
//                                   onClick={() => handleProjectClick(detail)}
//                                 >
//                                   <td className="p-2">{detail.project}</td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </div>
//                         </table>
//                       </div>
//                     </div>
//                   ))}
//               </div>
//             </div>
//           )}

//           {/* Speedometer Section */}
//           <Speedmetre />

//           {/* Location Selector Component */}
//           {/* <Locationselect onProjectsUpdate={setProjects} onCityUpdate={setSelectedCity} /> */}
//           <Graph/>

//           {/* Other Sections (excluding Total Projects) */}
//           {sections
//             .filter(section => section.title !== "Total Projects")
//             .map((section, sectionIdx) => (
//               <div key={sectionIdx}>
//                 <h2 className="text-xl font-semibold mb-4 text-white">{section.title}</h2>
//                 <div className="bg-gray-800 p-6 rounded-2xl shadow-lg w-full">
//                   {section.data.map((item, index) => (
//                     <div key={index} className="mb-1">
//                       <button
//                         className="w-full text-left p-3 bg-gray-200 rounded-lg font-medium"
//                         onClick={() => toggleSection(section.title, index)}
//                       >
//                         {item.name} - {item.count} 
//                       </button>
//                       <div
//                         style={{
//                           maxHeight: openIndex[section.title] === index ? "500px" : "0px",
//                           overflow: 'hidden',
//                           transition: "max-height 0.5s ease-in-out, padding 0.3s ease-in-out",
//                           background: "#f3f4f6",
//                           padding: openIndex[section.title] === index ? "12px" : "0px",
//                           borderRadius: "8px",
//                           marginTop: "8px",
//                         }}
//                       >
//                         <table className="w-full text-sm">
//                           <thead>
//                             <tr className="border-b">
//                               <th className="text-left p-2">Project Name</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {item.details.map((detail, idx) => (
//                               <tr key={idx} className="border-b hover:bg-gray-100 cursor-pointer" onClick={() => handleProjectClick(detail)}>
//                                 <td className="p-2">{detail.project_name}</td>
//                                 {/* <td className="p-2">{detail.sector}</td> */}
//                               </tr>
//                             ))}
//                           </tbody>
//                         </table>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             ))}

//           {/* World Map Card */}
//           {/* <Worldmapcard /> */}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Table3;
// // add new feature


// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import axios from 'axios'; // Import axios for API calls
// import Speedmetre from './nestedcomponent/speedmetre'; // Ensure this path is correct
// import Worldmapcard from './worldmapcard'; // Ensure this path is correct
// import Locationselect from './locationselect'; // Ensure this path is correct
// import Graph from '../Overview/graph'; // Ensure this path is correct

// const Table3 = () => {
//   const [openIndex, setOpenIndex] = useState({});
//   const [projects, setProjects] = useState([]);
//   const [selectedCity, setSelectedCity] = useState('');
//   const [sections, setSections] = useState([]); // State to store dynamic sections
//   const navigate = useNavigate();

//   // Fetch projects from the API
//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         const response = await axios.get('http://localhost:3002/api/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
//           }
//         });

//         // Log the API response to debug
//         console.log('API Response:', response.data);

//         // Access the `projects` array from the response
//         const projectsData = response.data.projects;

//         // Transform the API data into the required format for sections
//         const totalProjects = {
//           title: "Total Projects",
//           data: [
//             {
//               count: `${projectsData.length}+`,
//               details: projectsData.map(project => ({ ...project, project: project.project_name })) // Pass the entire project object
//             }
//           ]
//         };

//         // Dynamically generate sectors based on the data
//         const sectorCounts = projectsData.reduce((acc, project) => {
//           acc[project.sector] = (acc[project.sector] || 0) + 1;
//           return acc;
//         }, {});

//         const sectors = {
//           title: "SECTORS",
//           data: Object.keys(sectorCounts).map(sector => ({
//             name: sector,
//             count: sectorCounts[sector],
//             details: projectsData
//               .filter(project => project.sector === sector)
//               .map(project => ({ ...project, project: project.project_name })) // Pass the entire project object
//           }))
//         };

//         // Sort projects by cost to get the biggest projects
//         const sortedByCost = projectsData.sort((a, b) => b.cost - a.cost);
//         const biggestProjects = {
//           title: "BIGGEST PROJECTS",
//           data: sortedByCost.slice(0, 5).map(project => ({
//             // Remove the hyphen from the project name
//             count: `${project.project_name} (${project.sector}-${project.state}) $${project.cost}`,
//             details: projectsData
//               .filter(p => p.project_name === project.project_name) // Include all matching projects
//               .map(p => ({ ...p, project: p.project_name })) // Pass the entire project object
//           }))
//         };

//         // Set the sections with dynamic data
//         setSections([totalProjects, sectors, biggestProjects]);
//       } catch (error) {
//         console.error('Error fetching projects:', error);
//       }
//     };

//     fetchProjects();
//   }, []);

//   const toggleSection = (category, index) => {
//     setOpenIndex((prev) => ({
//       ...prev,
//       [category]: prev[category] === index ? null : index,
//     }));
//   };

//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } }); // Pass the entire project object
//   };

//   return (
//     <div
//       className="container-fluid p-3 flex-col lg:flex-row gap-5"
//       style={{
//         overflowY: 'auto',
//         height: '100%',
//         width: '100%',
//         scrollbarWidth: 'none',
//         msOverflowStyle: 'none'
//       }}
//     >
//       <div style={{ width: '100%', height: 'auto' }}>
//         <div className="w-full flex flex-col gap-3">

//           {/* Total Projects Section */}
//           {sections.some(section => section.title === "Total Projects") && (
//             <div>
//               <h2 className="text-xl font-semibold mb-4 text-white uppercase">Total Projects</h2>
//               <div className="bg-gray-800 p-6 rounded-2xl shadow-lg w-full">
//                 {sections
//                   .find(section => section.title === "Total Projects")
//                   ?.data.map((item, index) => (
//                     <div key={index} className="mb-1">
//                       <button
//                         className="w-full text-left p-3 bg-gray-200 rounded-lg font-medium"
//                         onClick={() => toggleSection("Total Projects", index)}
//                       >
//                         Total Projects - <span className="text-2xl font-bold">{item.count}</span>
//                       </button>
//                       <div
//                         style={{
//                           maxHeight: openIndex["Total Projects"] === index ? "500px" : "0px",
//                           overflow: 'hidden',
//                           transition: "max-height 0.5s ease-in-out, padding 0.3s ease-in-out",
//                           background: "#f3f4f6",
//                           padding: openIndex["Total Projects"] === index ? "12px" : "0px",
//                           borderRadius: "8px",
//                           marginTop: "8px",
//                         }}
//                       >
//                         <table className="w-full text-sm">
//                           <thead>
//                             <tr className="border-b">
//                               <th className="text-left p-2">Project Name</th>
//                             </tr>
//                           </thead>
//                           <div className="max-h-96 overflow-y-auto">
//                             <tbody>
//                               {item.details.map((detail, idx) => (
//                                 <tr
//                                   key={idx}
//                                   className="border-b hover:bg-gray-100 cursor-pointer"
//                                   onClick={() => handleProjectClick(detail)}
//                                 >
//                                   <td className="p-2">{detail.project}</td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </div>
//                         </table>
//                       </div>
//                     </div>
//                   ))}
//               </div>
//             </div>
//           )}

//           {/* Speedometer Section */}
//           <Speedmetre />

//           {/* Location Selector Component */}
//           {/* <Locationselect onProjectsUpdate={setProjects} onCityUpdate={setSelectedCity} /> */}
//           <Graph/>

//           {/* Other Sections (excluding Total Projects) */}
//           {sections
//             .filter(section => section.title !== "Total Projects")
//             .map((section, sectionIdx) => (
//               <div key={sectionIdx}>
//                 <h2 className="text-xl font-semibold mb-4 text-white">{section.title}</h2>
//                 <div className="bg-gray-800 p-6 rounded-2xl shadow-lg w-full">
//                   {section.data.map((item, index) => (
//                     <div key={index} className="mb-1">
//                       <button
//                         className="w-full text-left p-3 bg-gray-200 rounded-lg font-medium"
//                         onClick={() => toggleSection(section.title, index)}
//                       >
//                         {item.name} - {item.count} 
//                       </button>
//                       <div
//                         style={{
//                           maxHeight: openIndex[section.title] === index ? "500px" : "0px",
//                           overflow: 'hidden',
//                           transition: "max-height 0.5s ease-in-out, padding 0.3s ease-in-out",
//                           background: "#f3f4f6",
//                           padding: openIndex[section.title] === index ? "12px" : "0px",
//                           borderRadius: "8px",
//                           marginTop: "8px",
//                         }}
//                       >
//                         <table className="w-full text-sm">
//                           <thead>
//                             <tr className="border-b">
//                               <th className="text-left p-2">Project Name</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {item.details.map((detail, idx) => (
//                               <tr key={idx} className="border-b hover:bg-gray-100 cursor-pointer" onClick={() => handleProjectClick(detail)}>
//                                 <td className="p-2">{detail.project_name}</td>
//                                 {/* <td className="p-2">{detail.sector}</td> */}
//                               </tr>
//                             ))}
//                           </tbody>
//                         </table>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             ))}

//           {/* World Map Card */}
//           {/* <Worldmapcard /> */}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Table3;


// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import axios from 'axios'; // Import axios for API calls
// import Speedmetre from './nestedcomponent/speedmetre'; // Ensure this path is correct
// import Graph from '../Overview/graph'; // Ensure this path is correct

// const Table3 = () => {
//   // State to manage which section is open
//   const [openIndex, setOpenIndex] = useState({});

//   // State to store the list of projects
//   const [projects, setProjects] = useState([]);

//   // State to store the selected city (currently unused)
//   const [selectedCity, setSelectedCity] = useState('');

//   // State to store dynamic sections for the table
//   const [sections, setSections] = useState([]);

//   // Hook to navigate between routes
//   const navigate = useNavigate();





//   // Fetch projects from the API on component mount
//   useEffect(() => {
//     const fetchProjects = async () => {
//       try {
//         // Fetch all projects from the API
//         const response = await axios.get('http://localhost:3002/api/project/allProjects', {
//           headers: {
//             'x-company-id': '67ce66294f28f85e4ff91e2c'
//           }
//         });

//         // Log the API response for debugging
//         console.log('API Response:', response.data);

//         // Access the `projects` array from the response
//         const projectsData = response.data.projects;

//         // Transform the API data into the required format for sections

//         // Total Projects Section
//         const totalProjects = {
//           title: "Total Projects",
//           data: [
//             {
//               count: `${projectsData.length}+`,
//               details: projectsData.map(project => ({ ...project, project: project.project_name })) // Pass the entire project object
//             }
//           ]
//         };

//         // Dynamically generate sectors based on the data
//         const sectorCounts = projectsData.reduce((acc, project) => {
//           acc[project.sector] = (acc[project.sector] || 0) + 1;
//           return acc;
//         }, {});

//         // Log the sector counts for debugging
//         console.log('Sector Counts:', sectorCounts);

//         // Sectors Section
//         const sectors = {
//           title: "SECTORS",
//           data: Object.keys(sectorCounts).map(sector => ({
//             name: sector,
//             count: sectorCounts[sector],
//             sector, // Dynamically fetch icon for the sector
//             details: projectsData
//               .filter(project => project.sector === sector)
//               .map(project => ({ ...project, project: project.project_name })) // Pass the entire project object
//           }))
//         };

//         // Sort projects by cost to get the biggest projects
//         const sortedByCost = projectsData.sort((a, b) => b.cost - a.cost);

//         // Biggest Projects Section
//         const biggestProjects = {
//           title: "BIGGEST PROJECTS",
//           data: sortedByCost.slice(0, 5).map(project => {
//             // Log the original project name for debugging
//             console.log('Original Project Name:', project.project_name);

//             // Remove the hyphen from the project name
//             const cleanedProjectName = project.project_name.replace(/^-/, '');

//             // Log the cleaned project name for debugging
//             console.log('Cleaned Project Name:', cleanedProjectName);

//             return {
//               // Use the cleaned project name and convert cost to Indian Rupees
//               count: `${cleanedProjectName} (${project.sector}-${project.state}) ₹${(project.cost * 75).toLocaleString()}`, // Assuming 1 USD = 75 INR
//               details: projectsData
//                 .filter(p => p.project_name === project.project_name) // Include all matching projects
//                 .map(p => ({ 
//                   ...p, 
//                   project: p.project_name.replace(/^-/, '') // Pass the entire project object with hyphen removed
//                 }))
//             };
//           })
//         };

//         // Log the biggestProjects data for debugging
//         console.log('Biggest Projects Data:', biggestProjects);

//         // Set the sections with dynamic data
//         setSections([totalProjects, sectors, biggestProjects]);
//       } catch (error) {
//         console.error('Error fetching projects:', error);
//       }
//     };

//     fetchProjects();
//   }, []);

//   // Function to toggle the visibility of a section
//   const toggleSection = (category, index) => {
//     setOpenIndex((prev) => ({
//       ...prev,
//       [category]: prev[category] === index ? null : index,
//     }));
//   };

//   // Function to handle project click and navigate to the detail page
//   const handleProjectClick = (project) => {
//     navigate('/detail', { state: { project } }); // Pass the entire project object
//   };

//   return (
//     <div
//       className="container-fluid p-3 flex-col lg:flex-row gap-5"
//       style={{
//         overflowY: 'auto',
//         height: '100%',
//         width: '100%',
//         scrollbarWidth: 'none',
//         msOverflowStyle: 'none'
//       }}
//     >
//       <div style={{ width: '100%', height: 'auto' }}>
//         <div className="w-full flex flex-col gap-3">

//           {/* Total Projects Section */}
//           {sections.some(section => section.title === "Total Projects") && (
//             <div>
//               {/* <h2 className="text-xl font-semibold mb-4 text-white uppercase">Total Projects</h2> */}
//               <div className="bg-gray-800 p-6 rounded-2xl shadow-lg w-full">
//                 {sections
//                   .find(section => section.title === "Total Projects")
//                   ?.data.map((item, index) => (
//                     <div key={index} className="mb-1">
//                       <button
//                         className="w-full text-left p-3 bg-gray-200 rounded-lg font-medium"
//                         onClick={() => toggleSection("Total Projects", index)}
//                       >
//                         Total Projects - <span className="text-2xl font-bold">{item.count}</span>
//                       </button>
//                       <div
//                         style={{
//                           maxHeight: openIndex["Total Projects"] === index ? "500px" : "0px",
//                           overflow: 'hidden',
//                           transition: "max-height 0.5s ease-in-out, padding 0.3s ease-in-out",
//                           background: "#f3f4f6",
//                           padding: openIndex["Total Projects"] === index ? "12px" : "0px",
//                           borderRadius: "8px",
//                           marginTop: "8px",
//                         }}
//                       >
//                         <table className="w-full text-sm">
//                           <thead>
//                             <tr className="border-b">
//                               <th className="text-left p-2">Project Name</th>
//                             </tr>
//                           </thead>
//                           <div className="max-h-96 overflow-y-auto">
//                             <tbody>
//                               {item.details.map((detail, idx) => (
//                                 <tr
//                                   key={idx}
//                                   className="border-b hover:bg-gray-100 cursor-pointer"
//                                   onClick={() => handleProjectClick(detail)}
//                                 >
//                                   <td className="p-2">{detail.project}</td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </div>
//                         </table>
//                       </div>
//                     </div>
//                   ))}
//               </div>
//             </div>
//           )}

//           {/* Speedometer Section */}
//           <Speedmetre />

//           {/* Graph Component */}
//           <Graph/>

//           {/* Other Sections (excluding Total Projects) */}
//           {sections
//             .filter(section => section.title !== "Total Projects")
//             .map((section, sectionIdx) => (
//               <div key={sectionIdx}>
//                 <h2 className="text-xl font-semibold mb-4 text-white">{section.title}</h2>
//                 <div className="bg-gray-800 p-6 rounded-2xl shadow-lg w-full">
//                   {section.data.map((item, index) => (
//                     <div key={index} className="mb-1">
//                       <button
//                         className="w-full text-left p-3 bg-gray-200 rounded-lg font-medium flex items-center gap-2"
//                         onClick={() => toggleSection(section.title, index)}
//                       >
//                         {item.icon} {/* Display the dynamically fetched icon */}
//                         {item.name} - {item.count} 
//                       </button>
//                       <div
//                         style={{
//                           maxHeight: openIndex[section.title] === index ? "500px" : "0px",
//                           overflow: 'hidden',
//                           transition: "max-height 0.5s ease-in-out, padding 0.3s ease-in-out",
//                           background: "#f3f4f6",
//                           padding: openIndex[section.title] === index ? "12px" : "0px",
//                           borderRadius: "8px",
//                           marginTop: "8px",
//                         }}
//                       >
//                         <table className="w-full text-sm">
//                           <thead>
//                             <tr className="border-b">
//                               <th className="text-left p-2">Project Name</th>
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {item.details.map((detail, idx) => (
//                               <tr key={idx} className="border-b hover:bg-gray-100 cursor-pointer" onClick={() => handleProjectClick(detail)}>
//                                 <td className="p-2">{detail.project_name}</td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </table>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             ))}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Table3;

